import { useQuery } from '@tanstack/react-query'
import { TeachersService } from '@/shared/api/rest/services/teachers.service'
import { useUserTypes } from '@/shared/hooks/useUserTypes'
import { UserTypes } from '@/shared/types/auth'

/** Anything with a branch on it — a registry row, a pending request. */
type BranchScoped = { branchId?: string | null }

/**
 * Which teachers this admin is responsible for.
 *
 * A global admin oversees the whole church: `scopeBranchId` is null and nothing
 * is filtered. An admin teacher is responsible for their own branch and only
 * that — they have no way to know whether a request from another branch is
 * genuine, so approving it is not theirs to do.
 *
 * The branch comes from their own teacher record (`GET /teachers/me`) and
 * nothing else. Deliberately NOT the branch picker: that control exists to
 * re-point the check-in stats, and wiring it in here would mean switching
 * branches silently changed who a request belongs to.
 */
export const useTeacherScope = () => {
    const userTypes = useUserTypes()
    const isGlobalAdmin = userTypes.hasType(UserTypes.IS_GLOBAL_ADMIN)
    const isAdminTeacher = userTypes.hasAnyType([
        UserTypes.IS_ADMIN_TEACHER,
        UserTypes.IS_GLOBAL_ADMIN
    ])

    const { data: me, isLoading } = useQuery({
        // Shared with every other consumer of the signed-in teacher record.
        queryKey: ['teacher-me'],
        queryFn: () => TeachersService.me().catch(() => null),
        enabled: isAdminTeacher && !isGlobalAdmin,
        staleTime: 5 * 60_000
    })

    return {
        isAdminTeacher,
        isGlobalAdmin,
        /** The branch to filter by; null means unscoped (global admin). */
        scopeBranchId: isGlobalAdmin ? null : me?.branchId || null,
        /**
         * False while the admin's own branch is still being fetched. Callers
         * must not count or list anything yet: an unresolved branch looks
         * exactly like "no scope", which would flash the whole church's backlog
         * at a branch admin before settling.
         */
        ready: isGlobalAdmin || !isAdminTeacher || !isLoading
    }
}

/**
 * Whether one record falls inside the scope.
 *
 * A record with no branch is out of scope for a branch admin: it belongs to no
 * branch, so no branch admin can vouch for it, and a global admin — who sees
 * everything — is the one to deal with it.
 */
export const inTeacherScope = (
    record: BranchScoped,
    scopeBranchId: string | null
): boolean => !scopeBranchId || record.branchId === scopeBranchId
